import type { FunctionComponent } from 'react';
import { Fragment, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobCluster } from '~/shared/data-contract/job-cluster';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconDelete, IconExternalLink } from '~/ui/core/icons';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { useDeleteJobClusters } from '../api/use-delete-job-clusters';
import { WatchMenuItem } from '../components/WatchMenuItem';

export const JobClusterOptionsMenu: FunctionComponent<{
    readonly cluster: JobCluster;
    readonly onClose: () => void;
}> = ({ cluster, onClose }) => {
    const translate = useTranslate();
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();
    const { mutate: deleteJobClusters } = useDeleteJobClusters();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const handleShare = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            showSuccessSnackbar('pages.job-architecture-v2.job-profile-copied-to-clipboard');
        } catch (error) {
            showErrorSnackbar(error);
        }
    };

    const handleConfirmDelete = (): void => {
        onClose();
        setIsDeleteDialogOpen(false);
        deleteJobClusters({ jobClusters: [cluster] });
    };

    return (
        <Fragment>
            <DropdownMenu>
                <DropdownMenuTrigger>
                    <Button size="sm" variant="secondary">
                        {translate('common.options')}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={5} style={{ zIndex: 3000 }}>
                    <DropdownMenuItem onSelect={handleShare}>
                        <IconExternalLink size={16} />
                        {translate('common.share')}
                    </DropdownMenuItem>
                    <WatchMenuItem itemId={cluster.id} />
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        disabled={!cluster.actions.canEdit}
                        onSelect={(event) => {
                            event.preventDefault();
                            setIsDeleteDialogOpen(true);
                        }}
                    >
                        <IconDelete size={16} />
                        {translate('common.delete')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.delete')}
                confirmButtonVariant="destructive"
                description={translate('pages.job-architecture-v2.delete-job-cluster-description')}
                open={isDeleteDialogOpen}
                title={translate('pages.job-architecture-v2.delete-job-cluster')}
                onCancel={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
            />
        </Fragment>
    );
};
